"use client";

import * as React from "react";

/**
 * Keeps the last truthy `value` around for `ms` after it goes falsy, so a
 * conditionally-rendered overlay can run a CSS exit animation before React
 * unmounts it.
 *
 * Returns the value to render with (`null` once the exit animation is done) and
 * the animation state to stamp on the element as `data-state`.
 */
export function useExitDelay<T>(
  value: T,
  ms = 160,
): [T | null, "open" | "closed"] {
  const [held, setHeld] = React.useState<T | null>(value ? value : null);

  React.useEffect(() => {
    if (value) {
      setHeld(value);
      return undefined;
    }
    const timer = window.setTimeout(() => setHeld(null), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);

  return [value ? value : held, value ? "open" : "closed"];
}
